import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { stripeService } from "../services/stripeService";
import { paystackService } from "../services/paystackService";
import { openBankingService } from "../services/openBankingService";
import { bankDetailsService } from "../services/bankDetailsService";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CheckCircle, X, Send } from "lucide-react";

// Returns null instead of throwing — payment setup is optional, so a failed
// status lookup must never block sending the Pre-LBA.
const safe = async (fn) => {
  try {
    return await fn();
  } catch {
    return null;
  }
};

const hasBankDetails = (d) => !!(d && d.companyName && (d.accountNo || d.iban));

const formatSortCode = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  return d.length === 6 ? `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}` : v || "";
};

const LBAConfirmation = ({ onClose, formData }) => {
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [status, setStatus] = useState("");
  const [paymentLink, setPaymentLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const user = JSON.parse(localStorage.getItem("authUser"));

  // ── Work out how the debtor will pay ──
  // Priority:
  //   1. verified Open Banking payout account (backend renders those details)
  //   2. Paystack payment link (if connected and the active provider)
  //   3. Stripe payment link (only if connected AND onboarding complete)
  //   4. bank details saved in the payment modal (plain bank transfer)
  //   5. nothing — the Pre-LBA is still sent, just without payment details
  // A failed link creation falls through to the next option instead of
  // stopping the send.
  const resolvePaymentMethod = async (lbaId) => {
    setLoading(true);
    try {
      const obStatus = await safe(() => openBankingService.getStatus());
      if (obStatus?.status === "verified") {
        setPaymentLink(null);
        return { type: "open_banking", paymentLinkUrl: "", provider: "", bankDetails: null };
      }

      const [paystackStatus, stripeStatus] = await Promise.all([
        safe(() => paystackService.getAccountStatus()),
        safe(() => stripeService.getAccountStatus()),
      ]);

      const usePaystack = !!(paystackStatus?.connected && paystackStatus?.isActiveProvider);
      const useStripe =
        !usePaystack && !!(stripeStatus?.connected && stripeStatus?.onboardingComplete);

      if (usePaystack || useStripe) {
        try {
          const result = usePaystack
            ? await paystackService.createPaymentLink(lbaId)
            : await stripeService.createPaymentLink(lbaId);
          if (result?.paymentLinkUrl) {
            setPaymentLink(result.paymentLinkUrl);
            return {
              type: "link",
              paymentLinkUrl: result.paymentLinkUrl,
              provider: usePaystack ? "paystack" : "stripe",
              bankDetails: null,
            };
          }
        } catch (err) {
          console.warn("Payment link creation failed, falling back:", err);
        }
      }

      const bank = await safe(() => bankDetailsService.get());
      setPaymentLink(null);
      if (hasBankDetails(bank)) {
        return { type: "bank_transfer", paymentLinkUrl: "", provider: "", bankDetails: bank };
      }

      return { type: "none", paymentLinkUrl: "", provider: "", bankDetails: null };
    } finally {
      setLoading(false);
    }
  };

  const getFormattedDates = () => {
    const today = new Date();
    const formattedToday = today.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 15);
    const formattedFutureDate = futureDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    return { formattedToday, formattedFutureDate };
  };

  const generatePDF = useCallback(async () => {
    try {
      setIsGenerating(true);
      if (!formData.lbaId) throw new Error("LBA ID is required.");

      // Never throws for a missing provider — see resolvePaymentMethod.
      setStatus("Preparing payment details…");
      const payment = await resolvePaymentMethod(formData.lbaId);
      const createdPaymentLink = payment.paymentLinkUrl || "";
      const paymentProvider = payment.provider || "";

      setStatus("Generating PDF…");

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const primaryColor = [13, 99, 119];
      const neutralColor = [30, 30, 30];
      const mutedColor = [100, 116, 139];
      const linkColor = [37, 99, 235];
      const neutralLightColor = [250, 250, 250];
      const neutralBorderColor = [226, 226, 226];
      const whiteColor = [255, 255, 255];
      const offWhiteColor = [248, 248, 248];
      const advantageHeaderColor = [233, 240, 251];
      const advantageTextColor = [30, 45, 142];
      const consequenceHeaderColor = [255, 246, 234];
      const consequenceTextColor = [196, 92, 0];
      const boxBorderColor = [211, 211, 211];

      const { formattedToday, formattedFutureDate } = getFormattedDates();
      const pageWidth = 210,
        pageHeight = 297,
        margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      const effectivePageHeight = pageHeight - margin;
      let yPos = margin;

      const addRect = (x, y, w, h, fill, stroke) => {
        if (fill) pdf.setFillColor(...fill);
        if (stroke) pdf.setDrawColor(...stroke);
        pdf.rect(x, y, w, h, fill && stroke ? "FD" : fill ? "F" : "D");
      };

      const addText = (text, x, y, opts = {}) => {
        const {
          fontSize = 10,
          isBold = false,
          color = neutralColor,
          align = "left",
          maxWidth = contentWidth,
        } = opts;
        pdf.setFontSize(fontSize);
        pdf.setFont("helvetica", isBold ? "bold" : "normal");
        pdf.setTextColor(...color);
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y, { align });
        return lines.length * (fontSize * 0.35) + 2;
      };

      const checkAndAddPage = (spaceNeeded) => {
        if (yPos + spaceNeeded > effectivePageHeight) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Plain "6ELA" wordmark — client flagged the triangle glyph as odd,
      // use the letter A instead.
      const drawBrandMark = (x, y, opts = {}) => {
        const {
          fontSize = 16,
          color = neutralColor,
          isBold = true,
          align = "left",
        } = opts;
        pdf.setFontSize(fontSize);
        pdf.setFont("helvetica", isBold ? "bold" : "normal");
        pdf.setTextColor(...color);
        const label = "6ELA";
        const totalWidth = pdf.getTextWidth(label);
        let startX = x;
        if (align === "center") startX = x - totalWidth / 2;
        else if (align === "right") startX = x - totalWidth;
        pdf.text(label, startX, y);
        return totalWidth;
      };

      // Draws centered, word-wrapped text built from multiple segments,
      // where each segment can independently be bold or normal — used so a
      // single word/phrase (like a date) can be bold inside an otherwise
      // normal-weight centered paragraph.
      const addCenteredWrappedText = (
        segments,
        centerX,
        startY,
        maxWidth,
        opts = {},
      ) => {
        const { fontSize = 9, lineHeight = 4, color = neutralColor } = opts;
        pdf.setFontSize(fontSize);

        const words = [];
        segments.forEach((seg) => {
          seg.text.split(" ").forEach((w) => {
            if (w) words.push({ text: w, bold: seg.bold });
          });
        });

        pdf.setFont("helvetica", "normal");
        const spaceWidth = pdf.getTextWidth(" ");

        const lines = [];
        let currentLine = [];
        let currentWidth = 0;
        words.forEach((word) => {
          pdf.setFont("helvetica", word.bold ? "bold" : "normal");
          const wWidth = pdf.getTextWidth(word.text);
          const addWidth = currentLine.length ? spaceWidth + wWidth : wWidth;
          if (currentWidth + addWidth > maxWidth && currentLine.length) {
            lines.push(currentLine);
            currentLine = [word];
            currentWidth = wWidth;
          } else {
            currentLine.push(word);
            currentWidth += addWidth;
          }
        });
        if (currentLine.length) lines.push(currentLine);

        lines.forEach((line, i) => {
          let lineWidth = 0;
          line.forEach((word, idx) => {
            pdf.setFont("helvetica", word.bold ? "bold" : "normal");
            lineWidth += pdf.getTextWidth(word.text);
            if (idx < line.length - 1) lineWidth += spaceWidth;
          });
          let cursorX = centerX - lineWidth / 2;
          const y = startY + i * lineHeight;
          line.forEach((word) => {
            pdf.setFont("helvetica", word.bold ? "bold" : "normal");
            pdf.setTextColor(...color);
            pdf.text(word.text, cursorX, y);
            cursorX += pdf.getTextWidth(word.text) + spaceWidth;
          });
        });

        return lines.length;
      };

      // Left-aligned version of the segment wrapper, for the deadline callout.
      const addWrappedLeft = (segments, x, startY, maxWidth, opts = {}) => {
        const { fontSize = 9, lineHeight = 4, color = neutralColor } = opts;
        pdf.setFontSize(fontSize);
        const words = [];
        segments.forEach((seg) => {
          seg.text.split(" ").forEach((w) => {
            if (w) words.push({ text: w, bold: seg.bold });
          });
        });
        pdf.setFont("helvetica", "normal");
        const spaceWidth = pdf.getTextWidth(" ");
        const lines = [];
        let currentLine = [],
          currentWidth = 0;
        words.forEach((word) => {
          pdf.setFont("helvetica", word.bold ? "bold" : "normal");
          const wWidth = pdf.getTextWidth(word.text);
          const addWidth = currentLine.length ? spaceWidth + wWidth : wWidth;
          if (currentWidth + addWidth > maxWidth && currentLine.length) {
            lines.push(currentLine);
            currentLine = [word];
            currentWidth = wWidth;
          } else {
            currentLine.push(word);
            currentWidth += addWidth;
          }
        });
        if (currentLine.length) lines.push(currentLine);
        lines.forEach((line, i) => {
          let cursorX = x;
          const y = startY + i * lineHeight;
          line.forEach((word) => {
            pdf.setFont("helvetica", word.bold ? "bold" : "normal");
            pdf.setTextColor(...color);
            pdf.text(word.text, cursorX, y);
            cursorX += pdf.getTextWidth(word.text) + spaceWidth;
          });
        });
        return lines.length;
      };

      yPos = margin;

      // Correct aspect ratio for the new horizontal logo asset (2.24:1, no subline).
      const logoWidth = 34,
        logoHeight = 15.2;
      pdf.setFillColor(255, 255, 255);
      pdf.rect(margin, yPos, logoWidth, logoHeight, "F");
      try {
        let loaded = false;
        for (const path of [
          "assets/logo-horizontal-lba-form.png",
          "/assets/logo-horizontal-lba-form.png",
          `${window.location.origin}/assets/logo-horizontal-lba-form.png`,
        ]) {
          try {
            pdf.addImage(
              path,
              "PNG",
              margin,
              yPos,
              logoWidth,
              logoHeight,
              undefined,
              "FAST",
            );
            loaded = true;
            break;
          } catch {}
        }
        if (!loaded) throw new Error("Logo failed");
      } catch {
        pdf.setFillColor(...primaryColor);
        pdf.roundedRect(margin, yPos, logoWidth, logoHeight, 2, 2, "F");
        drawBrandMark(margin + logoWidth / 2, yPos + logoHeight / 2 + 3, {
          fontSize: 16,
          color: [255, 255, 255],
          align: "center",
        });
      }

      const refLabel = "REFERENCE";
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(...mutedColor);
      pdf.text(refLabel, margin + contentWidth, yPos + 3, {
        align: "right",
        charSpace: 0.3,
      });
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(15);
      pdf.setTextColor(...neutralColor);
      pdf.text(
        formData.autoIncrementAccount || "D9001",
        margin + contentWidth,
        yPos + 10,
        { align: "right" },
      );
      yPos += logoHeight + 5;

      pdf.setDrawColor(...neutralBorderColor);
      pdf.setLineWidth(0.3);
      pdf.line(margin, yPos, margin + contentWidth, yPos);
      yPos += 7;

      // Meta row: label above value, three columns
      const metaCols = [
        { label: "DATE", value: formattedToday },
        { label: "RECIPIENT", value: formData.name || "N/A" },
        { label: "BUSINESS", value: formData.businessName || "N/A" },
      ];
      const metaColWidth = contentWidth / metaCols.length;
      metaCols.forEach((col, i) => {
        const x = margin + i * metaColWidth;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(...mutedColor);
        pdf.text(col.label, x, yPos, { charSpace: 0.3 });
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10.5);
        pdf.setTextColor(...neutralColor);
        pdf.text(col.value, x, yPos + 6);
      });
      yPos += 13;

      // Address — shown as a single line under the meta row when available
      const addressParts = [
        formData.houseName,
        formData.street,
        formData.city,
        formData.country,
        formData.postcode,
      ].filter(Boolean);
      if (addressParts.length > 0) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(...mutedColor);
        pdf.text("ADDRESS", margin, yPos, { charSpace: 0.3 });
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9.5);
        pdf.setTextColor(...neutralColor);
        const addrLines = pdf.splitTextToSize(
          addressParts.join(", "),
          contentWidth,
        );
        pdf.text(addrLines, margin, yPos + 5.5);
        yPos += 9 + addrLines.length * 4.2;
      }

      // ── Hardened opening: notice title + subheading (replaces the old soft intro) ──
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(15);
      pdf.setTextColor(...consequenceTextColor);
      pdf.text("FINAL PRE-LEGAL NOTICE", margin, yPos, { charSpace: 0.2 });
      yPos += 6;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...neutralColor);
      pdf.text(
        "Action Required Before Legal Proceedings Are Considered",
        margin,
        yPos,
      );
      yPos += 7;

      // ── Outstanding Balance panel — fed from the invoice ──
      const balancePanelH = 22;
      addRect(
        margin,
        yPos,
        contentWidth,
        balancePanelH,
        offWhiteColor,
        neutralBorderColor,
      );
      const balanceCols = [
        {
          label: "OUTSTANDING BALANCE",
          value: `${formData.currency || "£"}${formData.amount || "0.00"}`,
          sub: `Invoice(s): ${formData.invoiceNumber || "N/A"}`,
        },
        {
          label: "PAYMENT DUE",
          value: formData.dueDate
            ? new Date(formData.dueDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })
            : "N/A",
        },
        {
          label: "STATUS",
          value: "Final Opportunity",
          sub: "Before Legal Escalation (Pre-LBA)",
        },
      ];
      const balanceColWidth = contentWidth / balanceCols.length;
      balanceCols.forEach((col, i) => {
        const x = margin + i * balanceColWidth + 6;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(...mutedColor);
        pdf.text(col.label, x, yPos + 6.5, { charSpace: 0.3 });
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(i === 0 ? 12.5 : 9.5);
        pdf.setTextColor(
          i === 0 ? consequenceTextColor[0] : neutralColor[0],
          i === 0 ? consequenceTextColor[1] : neutralColor[1],
          i === 0 ? consequenceTextColor[2] : neutralColor[2],
        );
        const valLines = pdf.splitTextToSize(col.value, balanceColWidth - 10);
        pdf.text(valLines, x, yPos + 12.5);
        if (col.sub) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(7);
          pdf.setTextColor(...mutedColor);
          pdf.text(col.sub, x, yPos + 12.5 + valLines.length * 4.2);
        }
      });
      yPos += balancePanelH + 6;

      const colWidth = (contentWidth - 12) / 2;

      // Renders a caps header + a tick-mark checklist in one column, and
      // returns the Y position after the last item so both columns can be
      // measured before the shared divider/box height is fixed.
      const addChecklistColumn = (
        label,
        items,
        x,
        labelColor,
        itemColor,
        subLabel,
      ) => {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9.5);
        pdf.setTextColor(...labelColor);
        const headerLines = pdf.splitTextToSize(label, colWidth - 4);
        pdf.text(headerLines, x, yPos + 8, { charSpace: 0.2 });
        let itemY = yPos + 8 + headerLines.length * 4.6 + 4;
        if (subLabel) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(7.5);
          pdf.setTextColor(...mutedColor);
          const subLines = pdf.splitTextToSize(subLabel, colWidth - 4);
          pdf.text(subLines, x, itemY);
          itemY += subLines.length * 3.6 + 2.5;
        }
        items.forEach((item) => {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8.5);
          pdf.setTextColor(...itemColor);
          pdf.text("\u2713", x, itemY);
          pdf.setFont("helvetica", "normal");
          pdf.setTextColor(...neutralColor);
          const lines = pdf.splitTextToSize(item, colWidth - 9);
          pdf.text(lines, x + 5, itemY);
          itemY += lines.length * 4.2 + 2.8;
        });
        return itemY;
      };

      // Left column — "Why Resolve This Now?"
      pdf.setFillColor(...primaryColor);
      pdf.rect(margin, yPos, colWidth, 0.8, "F");
      const whyEndY = addChecklistColumn(
        "WHY RESOLVE THIS NOW?",
        [
          "Avoid legal proceedings",
          "Prevent additional costs and interest",
          "Protect your business credit profile",
          "Resolve the matter quickly and professionally",
        ],
        margin,
        primaryColor,
        primaryColor,
      );

      // Right column — "Potential Consequences of Non-Payment"
      const consX = margin + colWidth + 12;
      pdf.setFillColor(...consequenceTextColor);
      pdf.rect(consX, yPos, colWidth, 0.8, "F");
      const consEndY = addChecklistColumn(
        "POTENTIAL CONSEQUENCES OF NON-PAYMENT",
        [
          "Formal legal proceedings",
          "Recovery of court fees",
          "Statutory interest",
          "Recoverable legal costs",
          "County Court Judgment (CCJ) (where applicable)",
          "Enforcement action following judgment",
        ],
        consX,
        consequenceTextColor,
        consequenceTextColor,
        "Failure to resolve this matter may result in:",
      );

      const sectionHeight = Math.max(whyEndY, consEndY) - yPos + 4;

      // Vertical divider between columns
      pdf.setDrawColor(...neutralBorderColor);
      pdf.setLineWidth(0.2);
      pdf.line(
        margin + colWidth + 6,
        yPos + 4,
        margin + colWidth + 6,
        yPos + sectionHeight - 4,
      );

      yPos += sectionHeight + 10;
      checkAndAddPage(40);

      // Urgency callout — filled warning tone, thick left bar, bold heading
      const urgencyBoxH = 26;
      addRect(
        margin,
        yPos,
        contentWidth,
        urgencyBoxH,
        consequenceHeaderColor,
        consequenceTextColor,
      );
      pdf.setFillColor(...consequenceTextColor);
      pdf.rect(margin, yPos, 2.2, urgencyBoxH, "F");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(...consequenceTextColor);
      pdf.text("ACTION REQUIRED", margin + 7, yPos + 6.5, { charSpace: 0.3 });

      // Rolling +15-day deadline from today, matching the on-screen
      // preview in LbaForm.js (getFormattedDates -> formattedFutureDate).
      const finalDeadline = formattedFutureDate;
      const noticeSegments = [
        {
          text: "Payment or a satisfactory response must be received no later than",
          bold: false,
        },
        { text: finalDeadline, bold: true },
        {
          text: "Failure to respond within this period may result in the matter being escalated for legal review without further notice.",
          bold: false,
        },
      ];
      pdf.setFontSize(9.5);
      let noticeLineCount = addWrappedLeft(
        noticeSegments,
        margin + 7,
        yPos + 12,
        contentWidth - 12,
        { fontSize: 9.5, lineHeight: 4.4, color: neutralColor },
      );
      const contactY = yPos + 12 + noticeLineCount * 4.4 + 3.5;
      const contactLines = pdf.splitTextToSize(
        "Reach us directly at hello@6ela.io to ensure prompt resolution before any further action.",
        contentWidth - 12,
      );
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(...consequenceTextColor);
      pdf.text(contactLines, margin + 7, contactY);
      yPos += Math.max(urgencyBoxH, contactY - yPos + 6) + 10;

      // ── How to pay — payment link OR bank transfer details ──
      // Nothing is drawn when no payment method is set up.
      if (payment.type === "link" && createdPaymentLink) {
        checkAndAddPage(30);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9.5);
        pdf.setTextColor(...primaryColor);
        pdf.text("HOW TO PAY", margin, yPos, { charSpace: 0.2 });
        yPos += 4;

        const linkBoxH = 17;
        addRect(margin, yPos, contentWidth, linkBoxH, advantageHeaderColor, boxBorderColor);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.8);
        pdf.setTextColor(...neutralColor);
        pdf.text("Pay securely online using the link below:", margin + 6, yPos + 6.5);
        const displayUrl =
          createdPaymentLink.length > 85
            ? `${createdPaymentLink.slice(0, 82)}...`
            : createdPaymentLink;
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...linkColor);
        pdf.textWithLink(displayUrl, margin + 6, yPos + 12, { url: createdPaymentLink });
        yPos += linkBoxH + 10;
      } else if (payment.type === "bank_transfer" && payment.bankDetails) {
        const b = payment.bankDetails;
        const reference = formData.invoiceNumber || formData.autoIncrementAccount || "";
        const rows = [
          ["Account name", b.companyName],
          ["Bank", b.bankName],
          ["Sort code", b.sortCode ? formatSortCode(b.sortCode) : ""],
          ["Account number", b.accountNo],
          ["IBAN", b.iban],
          ["Payment reference", reference],
        ].filter(([, value]) => value);

        const rowH = 5.6;
        const bankBoxH = 11 + rows.length * rowH + 2;
        checkAndAddPage(bankBoxH + 12);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9.5);
        pdf.setTextColor(...primaryColor);
        pdf.text("HOW TO PAY", margin, yPos, { charSpace: 0.2 });
        yPos += 4;

        addRect(margin, yPos, contentWidth, bankBoxH, offWhiteColor, neutralBorderColor);
        pdf.setFillColor(...primaryColor);
        pdf.rect(margin, yPos, 2.2, bankBoxH, "F");

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.8);
        pdf.setTextColor(...neutralColor);
        pdf.text("Please pay by bank transfer to the account below:", margin + 7, yPos + 6.5);

        rows.forEach(([label, value], i) => {
          const rowY = yPos + 12.5 + i * rowH;
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(...mutedColor);
          pdf.text(label, margin + 7, rowY);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(9);
          pdf.setTextColor(...neutralColor);
          pdf.text(String(value), margin + 50, rowY);
        });
        yPos += bankBoxH + 10;
      } else if (payment.type === "open_banking") {
        checkAndAddPage(14);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9.5);
        pdf.setTextColor(...primaryColor);
        pdf.text("HOW TO PAY", margin, yPos, { charSpace: 0.2 });
        yPos += 5.5;
        yPos += addText(
          "Bank transfer details for payment are included in the email accompanying this notice.",
          margin,
          yPos,
          { fontSize: 8.8, color: neutralColor },
        );
        yPos += 6;
      }

      // TODO: "Pay Now" QR code — parked per client request. Once we have a
      // QR asset generator for `createdPaymentLink`, drop it in here (e.g.
      // a small square code + "Scan to pay now" caption) before the
      // Evidence Status panel below.

      checkAndAddPage(30);

      // ── Digital Case Intelligence ──
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...primaryColor);
      pdf.text("DIGITAL CASE INTELLIGENCE", margin, yPos, { charSpace: 0.2 });
      yPos += 5.5;
      yPos += addText(
        "This matter is being managed through the 6ELA platform. All communications, payment activity, debtor engagement and supporting evidence are automatically recorded to create a complete digital chronology of the matter.",
        margin,
        yPos,
        { fontSize: 8.8, color: mutedColor },
      );
      yPos += 6;

      checkAndAddPage(35);

      // ── Evidence Status ──
      const evidenceItems = [
        "Outstanding balance verified",
        "Invoice history recorded",
        "Communication history retained",
        "Payment history recorded",
        "Supporting documentation available",
      ];
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...primaryColor);
      pdf.text("EVIDENCE STATUS", margin, yPos, { charSpace: 0.2 });
      let evY = yPos + 7;
      evidenceItems.forEach((item) => {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8.5);
        pdf.setTextColor(...primaryColor);
        pdf.text("\u2713", margin, evY);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8.8);
        pdf.setTextColor(...neutralColor);
        pdf.text(item, margin + 5, evY);
        evY += 5;
      });
      yPos = evY + 5;

      checkAndAddPage(20);

      // ── Branded sign-off block ──
      addText("Kind regards,", margin, yPos, { fontSize: 10, isBold: true });
      yPos += 4.5;

      const signoffMarkWidth = drawBrandMark(margin, yPos, {
        fontSize: 10,
        isBold: true,
      });
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(...neutralColor);
      pdf.text(
        " Automated Credit Control Platform",
        margin + signoffMarkWidth,
        yPos,
      );
      yPos += 5.5;

      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(8.5);
      pdf.setTextColor(...mutedColor);
      pdf.text(
        `Issued on behalf of ${formData.companyName || "{{CompanyName}}"}`,
        margin,
        yPos,
      );
      pdf.setFont("helvetica", "normal");
      yPos += 8;

      // ── Footer: platform line + Legal Escalation Ready side by side ──
      checkAndAddPage(30);
      pdf.setDrawColor(...neutralBorderColor);
      pdf.setLineWidth(0.3);
      pdf.line(margin, yPos, margin + contentWidth, yPos);
      yPos += 6;

      const footerColWidth = (contentWidth - 12) / 2;

      // Left: issued-via / powered-by / contact
      let leftY = yPos;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.setTextColor(55, 65, 81);
      const issuedViaPrefix = "Issued via the ";
      pdf.text(issuedViaPrefix, margin, leftY);
      const issuedViaPrefixWidth = pdf.getTextWidth(issuedViaPrefix);
      const issuedViaMarkWidth = drawBrandMark(
        margin + issuedViaPrefixWidth,
        leftY,
        { fontSize: 8.5, isBold: false, color: [55, 65, 81] },
      );
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.setTextColor(55, 65, 81);
      pdf.text(
        " Accounts Receivable Platform",
        margin + issuedViaPrefixWidth + issuedViaMarkWidth,
        leftY,
      );
      leftY += 5.5;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.setTextColor(55, 65, 81);
      const poweredByPrefix = "Powered by ";
      pdf.text(poweredByPrefix, margin, leftY);
      const poweredByPrefixWidth = pdf.getTextWidth(poweredByPrefix);
      const poweredLogoW = 15,
        poweredLogoH = 6.7;
      try {
        let loaded = false;
        for (const path of [
          "assets/logo-horizontal-lba-form.png",
          "/assets/logo-horizontal-lba-form.png",
          `${window.location.origin}/assets/logo-horizontal-lba-form.png`,
        ]) {
          try {
            pdf.addImage(
              path,
              "PNG",
              margin + poweredByPrefixWidth,
              leftY - poweredLogoH + 1.5,
              poweredLogoW,
              poweredLogoH,
              undefined,
              "FAST",
            );
            loaded = true;
            break;
          } catch {}
        }
        if (!loaded) throw new Error("Logo failed");
      } catch {
        drawBrandMark(margin + poweredByPrefixWidth, leftY, {
          fontSize: 8.5,
          isBold: true,
        });
      }
      leftY += 6.5;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.setTextColor(...linkColor);
      pdf.textWithLink("www.6ela.com", margin, leftY, {
        url: "https://www.6ela.com",
      });
      leftY += 4.5;
      pdf.textWithLink("support@6ela.com", margin, leftY, {
        url: "mailto:support@6ela.com",
      });

      // Right: Legal Escalation Ready badge + copy
      const rightX = margin + footerColWidth + 12;
      let rightY = yPos;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(...primaryColor);
      pdf.text("Litigation Ready\u2122 Case File", rightX, rightY, {
        charSpace: 0.2,
      });
      rightY += 5;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(...mutedColor);
      const escLines = pdf.splitTextToSize(
        "Every action is securely logged into a chronological, auditable case file. Where instructed, this can be shared with legal professionals to support pre-action review.",
        footerColWidth,
      );
      pdf.text(escLines, rightX, rightY);

      yPos += Math.max(leftY - yPos, escLines.length * 3.4 + 5) + 8;

      // ── Important Legal Notice ──
      checkAndAddPage(45);
      pdf.setDrawColor(...neutralBorderColor);
      pdf.setLineWidth(0.2);
      pdf.line(margin, yPos, margin + contentWidth, yPos);
      yPos += 6;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...neutralColor);
      pdf.text("Important Legal Notice", margin, yPos, { charSpace: 0.2 });
      yPos += 5.5;

      const legalNoticeParagraphs = [
        "This letter forms part of the creditor's formal pre-action recovery process. Failure to respond may result in the matter being referred to appointed legal professionals for review. Any subsequent legal proceedings may rely upon the documented evidence maintained within the 6ELA platform, including communications, invoices, payment history and supporting documentation.",
        "Where proceedings are commenced, the creditor may seek recovery of the outstanding balance together with any applicable statutory interest, court fees and recoverable legal costs.",
        "This communication has been issued via the 6ELA Accounts Receivable Platform on behalf of the creditor and should be treated as an important legal communication requiring immediate attention.",
      ];
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.3);
      pdf.setTextColor(...mutedColor);
      legalNoticeParagraphs.forEach((para) => {
        checkAndAddPage(16);
        const lines = pdf.splitTextToSize(para, contentWidth);
        pdf.text(lines, margin, yPos, { lineHeightFactor: 1.3 });
        yPos += lines.length * 3.7 + 3.5;
      });

      const pdfFilename = `LBA_Form_${formData.name ? formData.name.replace(/\s+/g, "_") : "document"}_${formData.invoiceNumber || "invoice"}.pdf`;
      const pdfFile = new File([pdf.output("blob")], pdfFilename, {
        type: "application/pdf",
      });

      setStatus("Sending email…");
      const emailFormData = new FormData();
      emailFormData.append("pdfFile", pdfFile);
      emailFormData.append("lbaId", formData.lbaId);
      emailFormData.append("recipientEmail", formData.email || "");
      emailFormData.append("recipientName", formData.name || "");
      emailFormData.append(
        "subject",
        `Urgent: Outstanding Account Balance – Invoice ${formData.invoiceNumber || "N/A"}`,
      );
      emailFormData.append("businessName", formData.businessName || "");
      emailFormData.append("amount", formData.amount || "");
      emailFormData.append("currency", formData.currency || "£");
      emailFormData.append("invoiceNumber", formData.invoiceNumber || "");
      // paymentLink/paymentProvider are empty strings unless a Stripe or
      // Paystack link was created. paymentMethod tells the backend which
      // block to render in the email: "link" | "bank_transfer" |
      // "open_banking" | "none". bankDetails is only sent for bank_transfer.
      emailFormData.append("paymentLink", createdPaymentLink);
      emailFormData.append("paymentProvider", paymentProvider);
      emailFormData.append("paymentMethod", payment.type);
      if (payment.type === "bank_transfer" && payment.bankDetails) {
        emailFormData.append("bankDetails", JSON.stringify(payment.bankDetails));
      }
      emailFormData.append("companyName", formData.companyName || "");
      emailFormData.append("userId", user?.id || "");
      if (formData.signatureImage) emailFormData.append("hasSignature", "true");

      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/api/lba/send-lba-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: emailFormData,
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to send email");
      }

      setIsGenerating(false);
      setStatus("");
      alert(`Email successfully sent to ${formData.email}`);
      navigate("/debtordashboard");
    } catch (error) {
      console.error("Error:", error);
      setIsGenerating(false);
      setStatus("");
      alert("Operation failed. " + error.message);
    }
  }, [formData, navigate, API_URL]);

  const detailRows = [
    { label: "Recipient", value: formData.name || "Not provided" },
    { label: "Business", value: formData.businessName || "Not provided" },
    { label: "Email", value: formData.email || "Not provided" },
    {
      label: "Invoice number",
      value: formData.invoiceNumber || "Not provided",
    },
    {
      label: "Amount due",
      value: formData.amount
        ? `${formData.currency || "£"} ${formData.amount}`
        : "Not provided",
      highlight: true,
    },
    {
      label: "Due date",
      value: formData.dueDate
        ? formData.dueDate.toLocaleDateString("en-GB")
        : "Not provided",
    },
  ];

  const isBusy = isGenerating || loading;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');
        .lbac-conf * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div
        className="lbac-conf"
        style={{
          background: "#fff",
          borderRadius: 16,
          border: "1px solid #E2E8F0",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
          width: 480,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px 16px",
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "#F0FDF4",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #BBF7D0",
                flexShrink: 0,
              }}
            >
              <CheckCircle size={20} style={{ color: "#059669" }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                Pre-LBA form ready
              </div>
              <div style={{ fontSize: 13, color: "#64748B", marginTop: 2 }}>
                {status ||
                  (isBusy
                    ? "Processing…"
                    : "Review details below before sending.")}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#ffffff",
              border: "1px solid #E2E8F0",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <X size={14} style={{ color: "#64748B" }} />
          </button>
        </div>

        {/* Detail rows */}
        <div style={{ padding: "16px 24px" }}>
          {detailRows.map((row, i) => (
            <div
              key={row.label}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "11px 0",
                borderBottom:
                  i < detailRows.length - 1 ? "1px solid #F1F5F9" : "none",
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: "#94A3B8",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {row.label}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: row.highlight ? 700 : 500,
                  color: row.highlight ? "#DC2626" : "#0F172A",
                  fontFamily: row.highlight
                    ? "'DM Mono', monospace"
                    : "inherit",
                  maxWidth: 260,
                  textAlign: "right",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid #E2E8F0",
            background: "#FAFBFD",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <button
            onClick={onClose}
            disabled={isBusy}
            style={{
              flex: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "9px 14px",
              background: "#ffffff",
              border: "1px solid #E2E8F0",
              borderRadius: 8,
              fontFamily: "DM Sans,sans-serif",
              fontSize: 13,
              fontWeight: 600,
              color: "#1E293B",
              cursor: isBusy ? "not-allowed" : "pointer",
              opacity: isBusy ? 0.5 : 1,
            }}
          >
            Cancel
          </button>

          <button
            onClick={generatePDF}
            disabled={isBusy}
            style={{
              flex: 2,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "9px 14px",
              border: "none",
              borderRadius: 8,
              background: isBusy
                ? "#93C5FD"
                : "linear-gradient(135deg,#2563EB,#1D4ED8)",
              fontFamily: "DM Sans,sans-serif",
              fontSize: 13,
              fontWeight: 700,
              color: "#fff",
              cursor: isBusy ? "not-allowed" : "pointer",
              boxShadow: isBusy ? "none" : "0 2px 8px rgba(37,99,235,0.3)",
            }}
          >
            {isBusy ? (
              <>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    border: "2px solid rgba(255,255,255,0.4)",
                    borderTopColor: "#fff",
                    borderRadius: "50%",
                    animation: "spin 0.7s linear infinite",
                    display: "inline-block",
                  }}
                />
                {status || "Processing…"}
              </>
            ) : (
              <>
                <Send size={14} />
                Send Pre-LBA email
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default LBAConfirmation;